import { useEffect } from 'react';
import { headTagsFor } from '../seo/page-metadata';
import type { PageMetadata } from '../seo/page-metadata';

export type { PageMetadata };

/**
 * Sets the page's title and its head tags after the bundle has loaded.
 *
 * **The tags it writes are the tags the build writes**, because both come from `headTagsFor`. That
 * was previously a matter of two implementations agreeing; it is now one description with two
 * consumers, which is the only version of "the same tag set a renderer would emit" that survives a
 * change to either.
 *
 * **It is still not the SEO the blog was built for, for the pages it cannot cover.** The four static
 * pages are written at build time and arrive with these tags already in the bytes. An article is not:
 * its HTML is produced when it is published, and until that is deployed an article shared on WhatsApp
 * still previews from whatever `index.html` carried.
 */
export function usePageMetadata(metadata: PageMetadata): void {
    const { title } = metadata;

    // The whole description as one dependency: every field of it feeds the same list of tags, and
    // eight separate dependencies is eight chances to add a ninth field and forget.
    const key = JSON.stringify(metadata);

    useEffect(() => {
        const previousTitle = document.title;
        document.title = title;

        // The build writes this page's tags into the served HTML and marks them. They have done
        // their job by the time this runs — a crawler that renders JavaScript would otherwise find
        // two canonical links and two descriptions, which is worse than the one it came for.
        document.head.querySelectorAll('[data-head="prerendered"]').forEach((element) => element.remove());

        const managed = headTagsFor(JSON.parse(key) as PageMetadata, window.location.origin).map((tag) => {
            const element = document.createElement(tag.element);

            for (const [name, value] of Object.entries(tag.attributes)) {
                element.setAttribute(name, value);
            }

            document.head.appendChild(element);

            return element;
        });

        return () => {
            document.title = previousTitle;

            // Every tag is created and removed here rather than reused. Editing the ones that came
            // with the served HTML would leave an article's description on the home page after a
            // client-side navigation away from it — and now that the served HTML really does carry
            // a page's own tags, that is a live hazard rather than a theoretical one.
            managed.forEach((element) => element.remove());
        };
    }, [title, key]);
}
